/**
 * The two formatting gestures a plain textarea can offer honestly.
 *
 * WHY MARKDOWN AND NOT A RICH TEXT EDITOR. Nothing in the admin components
 * provides one and nothing is installed, but that is the smaller reason. This
 * text is also handed to buildMailto(), which URL-encodes it as PLAIN TEXT
 * into a `mailto:` link -- so a body carrying `<strong>` reaches a drafted
 * email with the tags visible, while `**bold**` reaches it readably. Markdown
 * is already the fork's convention for prose a person writes: notes render
 * through misc/Markdown.tsx, marked and DOMPurify are installed and wired.
 *
 * Both functions return the new text AND the selection to restore, because a
 * toolbar that formats your words and then drops the cursor at the end of the
 * document is a toolbar you use once.
 */

export type Edit = { text: string; start: number; end: number };

/**
 * Wrap the selection in `**`, or unwrap it if it is already wrapped.
 *
 * TOGGLING RATHER THAN ALWAYS WRAPPING. A button that only ever adds turns
 * `**bold**` into `****bold****` on a second press, which renders as literal
 * asterisks around bold text -- and the only way back is counting them by
 * hand.
 *
 * WITH NOTHING SELECTED IT INSERTS AN EMPTY PAIR and puts the caret between
 * them, so pressing Bold and typing does what everybody expects.
 */
export function toggleBold(text: string, start: number, end: number): Edit {
  const M = "**";

  if (start === end) {
    return {
      text: text.slice(0, start) + M + M + text.slice(start),
      start: start + M.length,
      end: start + M.length,
    };
  }

  const selected = text.slice(start, end);

  // Already wrapped, inside the selection: **like this**
  if (
    selected.length >= M.length * 2 &&
    selected.startsWith(M) &&
    selected.endsWith(M)
  ) {
    const inner = selected.slice(M.length, -M.length);
    return {
      text: text.slice(0, start) + inner + text.slice(end),
      start,
      end: start + inner.length,
    };
  }

  // Already wrapped, just outside the selection: **|like this|**
  if (
    text.slice(start - M.length, start) === M &&
    text.slice(end, end + M.length) === M
  ) {
    return {
      text:
        text.slice(0, start - M.length) + selected + text.slice(end + M.length),
      start: start - M.length,
      end: end - M.length,
    };
  }

  return {
    text: text.slice(0, start) + M + selected + M + text.slice(end),
    start: start + M.length,
    end: end + M.length,
  };
}

/**
 * Turn the selected lines into a `- ` list, or back into plain lines.
 *
 * WHOLE LINES, ALWAYS. A bullet is a property of a line, so a selection that
 * starts mid-word still bullets the line it started in -- otherwise the
 * marker lands in the middle of a sentence and the list does not render.
 *
 * IT TOGGLES ON WHETHER EVERY LINE IS ALREADY BULLETED. Mixed selections
 * bullet the rest rather than stripping the ones that had it, which is what
 * somebody dragging across a half-finished list means.
 */
export function toggleBullets(text: string, start: number, end: number): Edit {
  const lineStart = text.lastIndexOf("\n", start - 1) + 1;
  const nextBreak = text.indexOf("\n", end);
  const lineEnd = nextBreak === -1 ? text.length : nextBreak;

  const block = text.slice(lineStart, lineEnd);
  const lines = block.split("\n");
  const bulleted = /^\s*[-*]\s+/;
  const allBulleted = lines.every((l) => l.trim() === "" || bulleted.test(l));

  const next = lines
    .map((line) => {
      if (line.trim() === "") return line;
      if (allBulleted) return line.replace(bulleted, "");
      // Already bulleted, in a selection where others are not: leave it. The
      // naive `- ${line}` turns "- one" into "- - one", which renders as a
      // list whose first item is a hyphen.
      return bulleted.test(line) ? line : `- ${line}`;
    })
    .join("\n");

  return {
    text: text.slice(0, lineStart) + next + text.slice(lineEnd),
    start: lineStart,
    end: lineStart + next.length,
  };
}
